import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, ArrowRight, Search, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import EditablePageIntro from '../components/EditablePageIntro';
import { splitSpecialties } from '../utils/specialty';
import { READINESS_CATEGORIES, getReadinessCategory } from '../utils/readiness';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PillarsPage() {
    const { t } = useTranslation();
    const [pillars, setPillars] = useState([]);
    const [loading, setLoading] = useState(true);
    // Site-settings overrides (fully custom heading/intro configured by admin) win
    // over the i18n defaults. If the admin hasn't set one, use the translation.
    const [heading, setHeading] = useState('');
    const [intro, setIntro] = useState('');
    // Search filters — populated from the actual pillar entries (no free-form typing).
    // Country and city are separate dropdowns; city narrows to whatever country is picked.
    const [query, setQuery] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    const [specialtyFilter, setSpecialtyFilter] = useState('');
    // Readiness filter is a Set of category keys ('A' | 'B' | 'C'). An empty
    // set means "show everyone". Picking multiple keys shows pillars tagged
    // with ANY of the picked categories (OR, not AND).
    const [readinessFilter, setReadinessFilter] = useState(new Set());

    useEffect(() => {
        (async () => {
            try {
                const [{ data: list }, { data: settings }] = await Promise.all([
                    axios.get(`${API_URL}/api/pillars`),
                    axios.get(`${API_URL}/api/site-settings`)
                ]);
                setPillars(list || []);
                if (settings?.pillars_page_heading) setHeading(settings.pillars_page_heading);
                if (settings?.pillars_page_intro) setIntro(settings.pillars_page_intro);
            } catch (err) {
                // silent
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Options derived from the pillars themselves — no admin list to maintain.
    // City list respects the country filter (empty country = every city).
    const { countryOptions, cityOptions, specialtyOptions } = useMemo(() => {
        const countries = new Set(), cities = new Set(), specs = new Set();
        pillars.forEach(p => {
            if (p.country) countries.add(p.country);
            if (p.city && (!countryFilter || p.country === countryFilter)) cities.add(p.city);
            splitSpecialties(p.specialty).forEach(s => specs.add(s));
        });
        return {
            countryOptions: Array.from(countries).sort(),
            cityOptions: Array.from(cities).sort(),
            specialtyOptions: Array.from(specs).sort()
        };
    }, [pillars, countryFilter]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return pillars.filter(p => {
            if (countryFilter && (p.country || '') !== countryFilter) return false;
            if (cityFilter && (p.city || '') !== cityFilter) return false;
            if (specialtyFilter) {
                const tags = splitSpecialties(p.specialty);
                if (!tags.includes(specialtyFilter)) return false;
            }
            if (readinessFilter.size > 0) {
                const pcats = Array.isArray(p.readiness_categories) ? p.readiness_categories : [];
                if (!pcats.some(c => readinessFilter.has(c))) return false;
            }
            if (q) {
                const hay = `${p.name} ${p.specialty || ''} ${p.city || ''} ${p.country || ''} ${p.bio || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [pillars, query, countryFilter, cityFilter, specialtyFilter, readinessFilter]);

    const toggleReadinessFilter = (key) => {
        setReadinessFilter((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const clearFilters = () => {
        setQuery('');
        setCountryFilter('');
        setCityFilter('');
        setSpecialtyFilter('');
        setReadinessFilter(new Set());
    };
    const hasFilters = query || countryFilter || cityFilter || specialtyFilter || readinessFilter.size > 0;

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />
            <main className="pt-24 pb-16" data-testid="pillars-page">
                <div className="max-w-7xl mx-auto px-6 sm:px-12">
                    <div className="text-center mb-12">
                        <p className="overline mb-4">{t('pillars.kicker')}</p>
                        <h1 className="heading-2 mb-6">{heading || t('pillars.heading')}</h1>
                        <EditablePageIntro
                            pageKey="pillars"
                            className="max-w-3xl mx-auto"
                            fallback={<p className="body-text">{intro || t('pillars.sub')}</p>}
                        />
                    </div>

                    {/* Search bar */}
                    {pillars.length > 0 && (
                        <div className="bg-white border border-[#E5E0D8] rounded-sm p-4 mb-10" data-testid="pillars-search">
                            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 items-center">
                                <div className="relative">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A68]" />
                                    <input
                                        type="text"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder={t('pillars.search_placeholder')}
                                        className="w-full pl-9 text-sm"
                                        data-testid="pillars-search-input"
                                    />
                                </div>
                                <select
                                    value={countryFilter}
                                    onChange={(e) => { setCountryFilter(e.target.value); setCityFilter(''); }}
                                    className="w-full text-sm"
                                    data-testid="pillars-filter-country"
                                >
                                    <option value="">{t('pillars.any_country')}</option>
                                    {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <select
                                    value={cityFilter}
                                    onChange={(e) => setCityFilter(e.target.value)}
                                    className="w-full text-sm"
                                    disabled={cityOptions.length === 0}
                                    data-testid="pillars-filter-city"
                                >
                                    <option value="">{t('pillars.any_city')}</option>
                                    {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <select
                                    value={specialtyFilter}
                                    onChange={(e) => setSpecialtyFilter(e.target.value)}
                                    className="w-full text-sm"
                                    data-testid="pillars-filter-specialty"
                                >
                                    <option value="">{t('pillars.any_specialty')}</option>
                                    {specialtyOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                {hasFilters && (
                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        className="flex items-center gap-1 text-sm text-[#5B2D8E] hover:underline"
                                        data-testid="pillars-clear-filters"
                                    >
                                        <X className="w-4 h-4" /> {t('pillars.clear')}
                                    </button>
                                )}
                            </div>
                            {/* Readiness category chips — multi-select OR filter */}
                            <div className="mt-3 pt-3 border-t border-[#E5E0D8] flex flex-wrap items-center gap-2" data-testid="pillars-readiness-filter">
                                <span className="text-xs uppercase tracking-widest text-[#4A4A68] mr-1">Audience readiness</span>
                                {READINESS_CATEGORIES.map(cat => {
                                    const active = readinessFilter.has(cat.key);
                                    return (
                                        <button
                                            key={cat.key}
                                            type="button"
                                            onClick={() => toggleReadinessFilter(cat.key)}
                                            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm border transition-colors ${active ? `${cat.badgeClass} border-current` : 'bg-white border-[#E5E0D8] text-[#4A4A68] hover:bg-[#F9F6F0]'}`}
                                            title={`${cat.title} — ${cat.subtitle}`}
                                            data-testid={`pillars-filter-readiness-${cat.key}`}
                                        >
                                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${cat.dotClass}`} />
                                            {cat.key} · {cat.title}
                                        </button>
                                    );
                                })}
                            </div>
                            {hasFilters && (
                                <p className="text-xs text-[#4A4A68] mt-3">
                                    Showing {visible.length} of {pillars.length} pillars
                                </p>
                            )}
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-16">
                            <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto"></div>
                        </div>
                    ) : visible.length === 0 ? (
                        <div className="text-center py-16 bg-white border border-[#E5E0D8] rounded-sm">
                            <p className="text-[#4A4A68]">
                                {pillars.length === 0 ? t('pillars.empty') : t('pillars.empty')}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="pillars-grid">
                            {visible.map(p => (
                                <Link
                                    key={p.id}
                                    to={`/pillars/${p.id}`}
                                    className="group block bg-white border border-[#E5E0D8] rounded-sm overflow-hidden hover:shadow-lg transition-shadow"
                                    data-testid={`pillar-card-${p.id}`}
                                >
                                    <div className="h-56 bg-gradient-to-br from-[#5B2D8E]/10 to-[#D8B4E2]/20 flex items-center justify-center">
                                        {p.profile_image_url ? (
                                            <img src={resolveImageUrl(p.profile_image_url)} alt={p.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-[#5B2D8E]/20 flex items-center justify-center">
                                                <span className="font-serif text-3xl text-[#5B2D8E]">
                                                    {(p.name || '?').charAt(0)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-start justify-between gap-3 mb-1">
                                            <h2 className="font-serif text-2xl text-[#1A1A2E] group-hover:text-[#5B2D8E] transition-colors">
                                                {p.name}
                                            </h2>
                                            {p.is_founder && (
                                                <span
                                                    className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 bg-[#5B2D8E] text-white rounded-sm"
                                                    data-testid={`pillar-founder-badge-${p.id}`}
                                                    title="Founder / Site steward"
                                                >
                                                    {t('pillars.founder')}
                                                </span>
                                            )}
                                        </div>
                                        {splitSpecialties(p.specialty).length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`pillar-specialties-${p.id}`}>
                                                {splitSpecialties(p.specialty).map(tag => (
                                                    <span
                                                        key={tag}
                                                        className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#5B2D8E]/30 text-[#5B2D8E] rounded-sm"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {Array.isArray(p.readiness_categories) && p.readiness_categories.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`pillar-readiness-${p.id}`}>
                                                {p.readiness_categories.map(key => {
                                                    const cat = getReadinessCategory(key);
                                                    if (!cat) return null;
                                                    return (
                                                        <span
                                                            key={key}
                                                            className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm border ${cat.badgeClass}`}
                                                            title={`${cat.title} — ${cat.subtitle}`}
                                                        >
                                                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${cat.dotClass}`} />
                                                            {cat.key} · {cat.title}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {(p.city || p.country) && (
                                            <p className="flex items-center gap-2 text-sm text-[#4A4A68] mb-3">
                                                <MapPin className="w-4 h-4" />
                                                {[p.city, p.country].filter(Boolean).join(', ')}
                                            </p>
                                        )}
                                        {p.bio && (
                                            <p className="text-sm text-[#4A4A68] line-clamp-3 mb-4">{p.bio}</p>
                                        )}
                                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#5B2D8E] group-hover:gap-3 transition-all">
                                            {t('pillars.view_profile')} <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
